import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSaasApiBaseUrl } from '../../../utils/saasApiBaseUrl';

import {
  CURRENT_BAA_VERSION,
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
  getLegalBaseUrl,
} from '@saas-foundation/shared-frontend/utils/legalVersions';

/**
 * Post-login BAA gate for orgs that still need acceptance (mirrors SaaSFoundation).
 */
const BaaAcceptPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState({ baa: false, terms: false, privacy: false });
  const legalBase = getLegalBaseUrl();

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const API_BASE_URL = getSaasApiBaseUrl();
    fetch(`${API_BASE_URL}/legal/baa/status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || 'Unable to load BAA status');
        if (cancelled) return;
        setStatus(data);
        if (data && !data.needs_baa) {
          navigate('/onboarding', { replace: true });
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load BAA status');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!accepted.baa || !accepted.terms || !accepted.privacy) {
      setError('Please accept the BAA, Terms of Service, and Privacy Policy to continue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getSaasApiBaseUrl();
      const res = await fetch(`${API_BASE_URL}/legal/baa/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          baa_accepted: true,
          terms_accepted: true,
          privacy_accepted: true,
          baa_version: CURRENT_BAA_VERSION,
          terms_version: CURRENT_TERMS_VERSION,
          privacy_version: CURRENT_PRIVACY_VERSION,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to record BAA acceptance');
      }
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to record BAA acceptance');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const canAccept = status?.can_accept_baa;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg rounded-lg bg-white border border-gray-200 shadow-sm p-8">
        <h1 className="text-2xl font-bold text-gray-900">Business Associate Agreement required</h1>
        <p className="mt-2 text-sm text-gray-600">
          {status?.organization_name
            ? `Before you can use ${status.organization_name} in Agentic RCM, an organization owner or admin must accept our BAA (HIPAA).`
            : 'Before you can use Agentic RCM with patient or claims data, your organization must accept our BAA (HIPAA).'}
        </p>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
        )}

        {!canAccept ? (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Waiting for an organization owner or admin to accept the BAA. You can sign out and try again later.
          </div>
        ) : (
          <form onSubmit={handleAccept} className="mt-6 space-y-4">
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={accepted.baa}
                onChange={(e) => setAccepted((s) => ({ ...s, baa: e.target.checked }))}
              />
              <span>
                I am authorized to bind this organization and accept the{' '}
                <a href={`${legalBase}/baa`} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  Business Associate Agreement
                </a>{' '}
                (version {CURRENT_BAA_VERSION}).
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={accepted.terms}
                onChange={(e) => setAccepted((s) => ({ ...s, terms: e.target.checked }))}
              />
              <span>
                I agree to the{' '}
                <a href={`${legalBase}/terms`} target="_blank" rel="noreferrer" className="text-blue-600 underline">Terms of Service</a>.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={accepted.privacy}
                onChange={(e) => setAccepted((s) => ({ ...s, privacy: e.target.checked }))}
              />
              <span>
                I agree to the{' '}
                <a href={`${legalBase}/privacy`} target="_blank" rel="noreferrer" className="text-blue-600 underline">Privacy Policy</a>.
              </span>
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Accept and continue'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-gray-500">
          <Link to="/login" className="text-blue-600 hover:text-blue-700">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default BaaAcceptPage;
